import { Box, Typography } from '@mui/material';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../CRM/kanban-board/crm-theme';
import { CampaignTable } from './components/campaign-table';
import { DateRangeFilter, Period } from './components/date-range-filter';
import { initialPeriod } from './components/initial-period';
import { DashboardService } from './services/dashboard.service';
import { ExportButton } from '@components/ExportButton';
import { datedFileName, downloadSpreadsheet } from '@functions/export-spreadsheet';
import { campaignExportRows } from './dashboard-export';
import { CampaignReport } from './services/responses/dashboard.response';

export const Campaigns = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [period, setPeriod] = useState<Period>(initialPeriod);
    const [report, setReport] = useState<CampaignReport | undefined>();

    const load = useCallback(async () => {
        setReport(undefined);

        try {
            const reply = await new DashboardService().campaigns(period.from, period.to);
            setReport(reply.data.data);
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        }
    }, [period]);

    useEffect(() => {
        load();
    }, [load]);

    // Sem credencial de anúncio a tela explica o que faz, em vez de tabela vazia parecendo defeito.
    const withoutAd = report !== undefined && !report.adsConfigured;

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('crmDashboard.campanhas')} />

            <Box
                sx={{
                    background: cc.pageBg,
                    borderRadius: 3,
                    p: 2,
                    display: 'grid',
                    gap: 2,
                }}
            >
                {!withoutAd && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, alignItems: 'flex-end' }}>
                        <DateRangeFilter value={period} onChange={setPeriod} stacked />
                        <ExportButton
                            title={t('exportar.campanhas')}
                            disabled={!report || report.campaigns.length === 0}
                            onClick={() => report && downloadSpreadsheet(datedFileName('campanhas'), [
                                { name: t('exportar.campanhas'), rows: campaignExportRows(report, t) },
                            ])}
                        />
                    </Box>
                )}

                {withoutAd ? (
                    <Box
                        sx={{
                            background: cc.cardBg,
                            border: `1px dashed ${cc.borderStrong}`,
                            borderRadius: 3,
                            p: 5,
                            textAlign: 'center',
                        }}
                    >
                        <CampaignIcon sx={{ fontSize: 42, color: cc.textMuted, mb: 1.5 }} />
                        <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ color: cc.textPrimary, mb: 0.8 }}
                        >
                            {t('crmDashboard.semIntegracao')}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: cc.textSecondary, maxWidth: 460, display: 'inline-block' }}
                        >
                            {t('crmDashboard.semIntegracaoAjuda')}
                        </Typography>
                    </Box>
                ) : (
                    <CampaignTable data={report?.campaigns} />
                )}
            </Box>
        </DashboardLayout>
    );
};
